import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';

export interface DetailedMeasurement {
  id: number;
  measurement: number | null;
  source_type: 'eagleview' | 'gaf_quick_measure' | 'roofr' | 'hover' | 'other';
  source_type_display: string;
  source_document: number | null;
  source_document_url: string | null;
  source_document_filename: string | null;
  is_external_upload?: boolean;
  parsing_status: 'pending' | 'processing' | 'completed' | 'failed';
  parsing_status_display: string;
  parsing_error: string | null;
  total_area_sqft: number | null;
  total_squares: number | null;
  facet_count: number | null;
  waste_factor: number | null;
  predominant_pitch: string;
  total_ridges_ft: number | null;
  total_valleys_ft: number | null;
  total_eaves_ft: number | null;
  total_rakes_ft: number | null;
  total_hips_ft: number | null;
  total_perimeter_ft: number | null;
  measurement_date: string | null;
  image_date: string | null;
  data_quality_score: number | null;
  raw_parsed_data: Record<string, unknown>;
  ai_model: string;
  processing_time: number | null;
  tokens_used: number | null;
  created_at: string;
  updated_at: string;
}

export interface UploadMeasurementPdfResponse {
  success: boolean;
  message: string;
  data: DetailedMeasurement;
}

// Hook to fetch detailed measurements for a specific measurement
export function useDetailedMeasurements(measurementId: number) {
  return useQuery({
    queryKey: ['detailedMeasurements', measurementId],
    queryFn: async () => {
      const response = await api.get<{ results: DetailedMeasurement[] }>(`/api/measurements/${measurementId}/detailed-measurements/`);
      return response.data.results;
    },
    enabled: !!measurementId,
  });
}

// Hook to fetch detailed measurements for an opportunity
export function useDetailedMeasurementsByOpportunity(opportunityId: number) {
  return useQuery({
    queryKey: ['detailedMeasurementsByOpportunity', opportunityId],
    queryFn: async () => {
      const response = await api.get<{ results: DetailedMeasurement[] }>(`/api/measurements/detailed-measurements-by-opportunity/?opportunity_id=${opportunityId}`);
      return response.data.results;
    },
    enabled: !!opportunityId,
  });
}

// Hook to fetch detailed measurements for a job
export function useDetailedMeasurementsByJob(jobId: number) {
  return useQuery({
    queryKey: ['detailedMeasurementsByJob', jobId],
    queryFn: async () => {
      const response = await api.get<{ results: DetailedMeasurement[] }>(`/api/measurements/detailed-measurements-by-job/?job_id=${jobId}`);
      return response.data.results;
    },
    enabled: !!jobId,
  });
}

// Hook to upload and parse a measurement PDF
export function useUploadMeasurementPdf() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      file: File;
      measurementId?: number;
      opportunityId?: number;
      jobId?: number;
      replaceExisting?: boolean;
    }): Promise<UploadMeasurementPdfResponse> => {
      const formData = new FormData();
      formData.append('file', data.file);
      if (data.measurementId) {
        formData.append('measurement_id', data.measurementId.toString());
      }
      if (data.opportunityId) {
        formData.append('opportunity_id', data.opportunityId.toString());
      }
      if (data.jobId) {
        formData.append('job_id', data.jobId.toString());
      }
      if (data.replaceExisting) {
        formData.append('replace_existing', 'true');
      }

      const response = await api.post<UploadMeasurementPdfResponse>('/api/measurements/upload-measurement-pdf/', formData);
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['measurements'] });
      queryClient.invalidateQueries({ queryKey: ['detailedMeasurements'] });
      if (variables.opportunityId) {
        queryClient.invalidateQueries({
          queryKey: ['detailedMeasurementsByOpportunity', variables.opportunityId]
        });
        queryClient.invalidateQueries({
          queryKey: ['opportunityMeasurementDocs', variables.opportunityId.toString()]
        });
      }
      if (variables.jobId) {
        queryClient.invalidateQueries({
          queryKey: ['detailedMeasurementsByJob', variables.jobId]
        });
        queryClient.invalidateQueries({
          queryKey: ['jobMeasurementDocs', variables.jobId.toString()]
        });
      }
      if (data.data?.measurement) {
        queryClient.invalidateQueries({
          queryKey: ['measurement', data.data.measurement]
        });
        queryClient.invalidateQueries({
          queryKey: ['detailedMeasurements', data.data.measurement]
        });
      }
    },
  });
}

// Hook to delete an uploaded DetailedMeasurement (and its associated PDF document)
export function useDeleteDetailedMeasurement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id }: { id: number; opportunityId?: number }) => {
      await api.delete(`/api/measurements/detailed-measurements/${id}/`);
      return id;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['measurements'] });
      queryClient.invalidateQueries({ queryKey: ['detailedMeasurements'] });
      if (variables.opportunityId) {
        queryClient.invalidateQueries({
          queryKey: ['detailedMeasurementsByOpportunity', variables.opportunityId],
        });
        queryClient.invalidateQueries({
          queryKey: ['opportunityMeasurementDocs', variables.opportunityId.toString()],
        });
      }
    },
  });
}
